(() => {
  const registry = new WeakMap();
  const videoStates = new Map();
  let observer;
  let mediaObserver;
  let progressRaf = 0;
  let listening = false;
  let pageScroller;

  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = () => Boolean(window.Shopify && Shopify.designMode);

  function fillSegments(panel, progress) {
    const bars = panel.querySelectorAll('.v3__segments i');
    bars.forEach((bar, index) => {
      const fill = Math.max(0, Math.min(1, progress * bars.length - index));
      bar.style.setProperty('--fill', fill.toFixed(3));
    });
    const label = panel.querySelector('.v3__frame-label');
    if (label) label.textContent = `SCROLL ${String(Math.round(progress * 100)).padStart(3, '0')}`;
  }

  function getPanelProgress(panel) {
    const rect = panel.getBoundingClientRect();
    const distance = Math.max(1, panel.offsetHeight - innerHeight);
    return Math.max(0, Math.min(1, -rect.top / distance));
  }

  function getPageScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const overflow = getComputedStyle(wrapper).overflowY;
      if (overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay') return wrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  function getScrollMetrics() {
    const scroller = pageScroller || getPageScroller();
    const documentScroller = scroller === document.documentElement || scroller === document.body;
    const top = documentScroller ? window.scrollY : scroller.scrollTop;
    const height = documentScroller ? document.documentElement.scrollHeight : scroller.scrollHeight;
    const viewport = documentScroller ? innerHeight : scroller.clientHeight;
    return { top, max: Math.max(0, height - viewport) };
  }

  function seekVideoFrame(state, immediate = false) {
    if (!state.hydrated || !state.ready) return;
    const progress = getPanelProgress(state.panel);
    const duration = Math.max(.01, state.video.duration - .04);
    state.target = progress * duration;
    fillSegments(state.panel, progress);

    if (state.panel.classList.contains('v3__ai')) {
      state.panel.style.setProperty('--ai-progress', progress.toFixed(4));
      state.panel.style.setProperty('--burst', Math.sin(progress * Math.PI).toFixed(4));
    }

    const threshold = immediate ? .006 : Math.max(.012, duration / 360);
    if (state.video.seeking) {
      state.pending = true;
      return;
    }
    if (Math.abs(state.video.currentTime - state.target) <= threshold) return;
    state.pending = false;
    state.video.currentTime = state.target;
  }

  function hydrateVideo(state) {
    if (!state || state.hydrated) return;
    state.hydrated = true;
    const useMobile = matchMedia('(max-width: 749px)').matches;
    state.video.querySelectorAll('source').forEach((source) => {
      const selected = useMobile ? source.dataset.v32SrcMobile : source.dataset.v32SrcDesktop;
      const fallback = source.dataset.v32Src;
      if (!source.getAttribute('src') && (selected || fallback)) {
        source.setAttribute('src', selected || fallback);
      }
    });
    state.video.preload = 'metadata';
    state.video.setAttribute('preload', 'metadata');
    state.panel.classList.add('is-media-loading');
    state.video.load();
  }

  function setupVideo(panel, cleanups) {
    const video = panel.querySelector('video');
    if (!video) return;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.loop = false;
    video.removeAttribute('autoplay');
    video.pause();
    video.preload = 'none';
    video.setAttribute('preload', 'none');

    const state = { panel, video, hydrated: false, ready: false, target: 0, pending: false };
    videoStates.set(panel, state);
    const prepare = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return;
      video.pause();
      state.ready = true;
      panel.classList.remove('is-media-loading');
      panel.classList.add('is-media-ready');
      seekVideoFrame(state, true);
    };
    const completeSeek = () => {
      if (!state.ready) return;
      if (state.pending || Math.abs(video.currentTime - state.target) > Math.max(.012, video.duration / 360)) {
        state.pending = false;
        seekVideoFrame(state, true);
      }
    };
    video.addEventListener('loadedmetadata', prepare);
    video.addEventListener('loadeddata', prepare);
    video.addEventListener('canplay', prepare);
    video.addEventListener('durationchange', prepare);
    video.addEventListener('seeked', completeSeek);
    if (video.readyState >= 1) {
      state.hydrated = true;
      prepare();
    }
    mediaObserver?.observe(panel);
    if (panel.classList.contains('v3__hero')) hydrateVideo(state);
    cleanups.push(() => {
      video.pause();
      video.removeEventListener('loadedmetadata', prepare);
      video.removeEventListener('loadeddata', prepare);
      video.removeEventListener('canplay', prepare);
      video.removeEventListener('durationchange', prepare);
      video.removeEventListener('seeked', completeSeek);
      mediaObserver?.unobserve(panel);
      videoStates.delete(panel);
    });
  }

  function setupCarousel(panel, cleanups) {
    const slides = [...panel.querySelectorAll('.v3__slide')];
    const dots = [...panel.querySelectorAll('.v3__dots button')];
    if (!slides.length) return;
    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    const render = (index) => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === active);
        let delta = i - active;
        if (delta > 1) delta -= slides.length;
        if (delta < -1) delta += slides.length;
        slide.dataset.position = delta === -1 ? 'left' : delta === 1 ? 'right' : delta === 0 ? 'center' : 'far';
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    };
    const prev = panel.querySelector('.v3__arrow--prev');
    const next = panel.querySelector('.v3__arrow--next');
    const onPrev = () => render(active - 1);
    const onNext = () => render(active + 1);
    prev?.addEventListener('click', onPrev);
    next?.addEventListener('click', onNext);
    cleanups.push(() => prev?.removeEventListener('click', onPrev));
    cleanups.push(() => next?.removeEventListener('click', onNext));
    dots.forEach((dot, index) => {
      const click = () => render(index);
      dot.addEventListener('click', click);
      cleanups.push(() => dot.removeEventListener('click', click));
    });
    render(active);
  }

  function setupSecure(panel, cleanups) {
    const buttons = [...panel.querySelectorAll('.v3__hotspot')];
    if (!buttons.length) return;
    const reset = () => {
      panel.dataset.focus = 'none';
      buttons.forEach((button) => button.classList.remove('is-active'));
    };
    buttons.forEach((button) => {
      const click = () => {
        const same = panel.dataset.focus === button.dataset.focus;
        reset();
        if (!same) {
          panel.dataset.focus = button.dataset.focus;
          button.classList.add('is-active');
        }
      };
      button.addEventListener('click', click);
      cleanups.push(() => button.removeEventListener('click', click));
    });
    const close = panel.querySelector('.v3__focus-close');
    close?.addEventListener('click', reset);
    cleanups.push(() => close?.removeEventListener('click', reset));
  }

  function setupHeader(root, cleanups) {
    const nav = root.querySelector('.v3__nav');
    const menu = root.querySelector('.v3__menu');
    if (!nav || !menu) return;
    const toggle = () => {
      const open = nav.classList.toggle('is-open');
      menu.setAttribute('aria-expanded', String(open));
    };
    menu.addEventListener('click', toggle);
    cleanups.push(() => menu.removeEventListener('click', toggle));
    root.querySelectorAll('a[href^="#"]').forEach((link) => {
      const click = () => {
        nav.classList.remove('is-open');
        menu.setAttribute('aria-expanded', 'false');
      };
      link.addEventListener('click', click);
      cleanups.push(() => link.removeEventListener('click', click));
    });
  }

  function initRoot(root) {
    if (!root || registry.has(root)) return;
    const cleanups = [];
    setupHeader(root, cleanups);
    if (root.matches('[data-v32-video-panel]')) setupVideo(root, cleanups);
    if (root.matches('[data-v32-carousel]')) setupCarousel(root, cleanups);
    if (root.matches('[data-v32-secure]')) setupSecure(root, cleanups);
    registry.set(root, () => cleanups.splice(0).forEach((cleanup) => cleanup()));
    observer?.observe(root);
  }

  function destroyRoot(root) {
    observer?.unobserve(root);
    mediaObserver?.unobserve(root);
    registry.get(root)?.();
    registry.delete(root);
  }

  function onMediaApproach(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      hydrateVideo(videoStates.get(entry.target));
      mediaObserver?.unobserve(entry.target);
    });
  }

  function onIntersect(entries) {
    entries.forEach((entry) => {
      const panel = entry.target;
      panel.classList.toggle('is-v32-active', entry.isIntersecting);
      const video = panel.querySelector('video');
      if (video) video.pause();
      const state = videoStates.get(panel);
      if (state && entry.isIntersecting) {
        hydrateVideo(state);
        seekVideoFrame(state, true);
      }
    });
  }

  function updatePage() {
    progressRaf = 0;
    const progress = document.querySelector('.v3__progress span');
    if (progress) {
      const metrics = getScrollMetrics();
      progress.style.transform = `scaleX(${metrics.max > 0 ? metrics.top / metrics.max : 0})`;
    }
    videoStates.forEach((state) => seekVideoFrame(state));
  }

  function schedulePageUpdate() {
    if (!progressRaf) progressRaf = requestAnimationFrame(updatePage);
  }

  function initPage() {
    const bridge = document.querySelector('[data-v34-horizon]');
    if (!bridge) return;
    const snapEnabled = bridge.dataset.scrollSnap === 'true' && !designMode();
    document.documentElement.classList.toggle('v32-scroll', snapEnabled);
    document.documentElement.dataset.v34SnapMode = bridge.dataset.snapMode || 'mandatory';
    document.body.classList.add('v32-page');
    document.body.classList.toggle('v34-glass-nav', bridge.dataset.glassNavigation === 'true');
    if (designMode()) document.documentElement.classList.add('shopify-design-mode');
    pageScroller = getPageScroller();
    document.querySelectorAll('[data-v32-root], [data-v32-section]').forEach((root) => {
      registry.get(root)?.();
      registry.delete(root);
    });
    observer?.disconnect();
    mediaObserver?.disconnect();
    observer = new IntersectionObserver(onIntersect, { threshold: reduced() ? .01 : .12 });
    mediaObserver = new IntersectionObserver(onMediaApproach, {
      rootMargin: reduced() ? '40% 0px' : '125% 0px',
      threshold: .01
    });
    document.querySelectorAll('[data-v32-root], [data-v32-section]').forEach(initRoot);
    if (!listening) {
      addEventListener('scroll', schedulePageUpdate, { passive: true });
      document.querySelector('.page-wrapper')?.addEventListener('scroll', schedulePageUpdate, { passive: true });
      addEventListener('resize', () => {
        pageScroller = getPageScroller();
        schedulePageUpdate();
      }, { passive: true });
      listening = true;
    }
    updatePage();
  }

  document.addEventListener('DOMContentLoaded', initPage, { once: true });
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('[data-v34-horizon]')) {
      initPage();
      return;
    }
    const root = event.target.querySelector('[data-v32-root], [data-v32-section]') || event.target;
    initRoot(root);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-v32-root], [data-v32-section]') || event.target;
    destroyRoot(root);
  });
  document.addEventListener('shopify:section:reorder', initPage);
  document.addEventListener('shopify:section:select', (event) => {
    const root = event.target.querySelector('[data-v32-section]');
    root?.scrollIntoView({ behavior: designMode() ? 'auto' : 'smooth', block: 'start' });
  });
})();
